/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

// Material MUI
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  useMediaQuery
} from '@mui/material';

// Material Icons
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

// Components
import Heading from '../components/heading/Heading';
import msgNotificacion from '../scripts/notification';
import Formatter from '../scripts/formatters';

// Redux
import { NAME_PROJECT } from '../redux/constants';
import { useDispatch } from 'react-redux';
import { showProgress } from '../redux/reducers/progress.Slice';

// Services
import EmpleadoService from '../services/empleadoService';

export default function Perfiles() {
  const dispatch = useDispatch()
  const isMobile = useMediaQuery('(max-width: 740px)')
  const [perfiles, setPerfiles] = useState([])
  const [cargos, setCargos] = useState([])
  const [cargoSeleccionado, setCargoSeleccionado] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Cargar cargos disponibles al iniciar
  const cargarCargos = async () => {
    try {
      setLoading(true)
      const response = await EmpleadoService.getCargosDisponibles()
      
      if (response && response.Codigo === 1) {
        // Formatear cargos para Autocomplete
        const cargosFormateados = response.Data.map(cargo => ({
          id: cargo.Cargo,
          label: cargo.Cargo
        }))
        setCargos(cargosFormateados)
      } else {
        msgNotificacion('Error al cargar cargos disponibles', 'error')
      }
    } catch (error) {
      console.error('Error al cargar cargos:', error)
      msgNotificacion('Error al cargar cargos disponibles', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Cargar perfiles de empleados
  const cargarPerfiles = async (cargo = null) => {
    try {
      setLoading(true)
      setError(null)
      
      const dataRequest = { cargo: cargo?.id || '' }
      const response = await EmpleadoService.getAllPerfiles(dataRequest)
      
      if (response && response.Codigo === 1) {
        // Filtrar solo empleados activos (Estado = 'S')
        const perfilesActivos = response.Data.filter(perfil => perfil.ACTIVO === 'S');
        
        // Si no hay cargo seleccionado (TODOS LOS CARGOS), agrupar por área
        if (!cargo || !cargo.id) {
          // Agrupar empleados por área
          const empleadosPorArea = {}
          perfilesActivos.forEach(perfil => {
            const area = perfil.Area || 'Sin Área'
            if (!empleadosPorArea[area]) {
              empleadosPorArea[area] = []
            }
            empleadosPorArea[area].push(perfil)
          })
          
          // Convertir a array para el renderizado
          const perfilesAgrupados = Object.entries(empleadosPorArea).map(([area, empleados]) => ({
            area,
            empleados
          }))
          
          setPerfiles(perfilesAgrupados)
        } else {
          // Si hay cargo seleccionado, mostrar como antes (sin agrupar)
          setPerfiles(perfilesActivos)
        }
      } else if (response && response.Codigo === 200) {
        setPerfiles([])
        setError('No se encontraron empleados con el cargo especificado')
      } else {
        msgNotificacion(response?.Mensaje || 'Error al cargar perfiles', 'error')
        setPerfiles([])
      }
    } catch (error) {
      console.error('Error al cargar perfiles:', error)
      msgNotificacion('Error al cargar perfiles de empleados', 'error')
      setPerfiles([])
    } finally {
      setLoading(false)
    }
  }

  // Actualizar URL con el filtro
  const actualizarURL = (cargo) => {
    if (cargo && cargo.id) {
      window.history.pushState({}, '', `/perfiles?cargo=${encodeURIComponent(cargo.id)}`)
    } else {
      window.history.pushState({}, '', '/perfiles')
    }
  }

  // Cargar filtro desde URL
  const cargarFiltroDesdeURL = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const cargoFromUrl = urlParams.get('cargo')
    
    if (cargoFromUrl) {
      // Buscar el cargo en la lista de cargos disponibles
      const cargoEncontrado = cargos.find(cargo => cargo.id === cargoFromUrl)
      if (cargoEncontrado) {
        setCargoSeleccionado(cargoEncontrado)
        cargarPerfiles(cargoEncontrado)
      }
    }
  }

  // Manejar cambio de cargo
  const handleCargoChange = (event, newValue) => {
    setCargoSeleccionado(newValue)
    cargarPerfiles(newValue)
    actualizarURL(newValue)
  }

  // Abrir perfil del empleado
  const abrirPerfilEmpleado = (codEmpleado) => {
    try {
      localStorage.setItem('empleadoSelected', codEmpleado)
      
      // Si es móvil, abre en la misma pestaña
      if (isMobile) {
        window.location.href = '/miPerfil'
      } else {
        // Si es desktop, abre en nueva pestaña
        window.open('/miPerfil', '_blank')
      }
    } catch (error) {
      console.error('Error al abrir perfil:', error)
    }
  }

  // Calcular años y meses de servicio
  const calcularAniosServicio = (fechaIngreso) => {
    if (!fechaIngreso) return null
    
    try {
      const fechaIng = new Date(fechaIngreso)
      const fechaActual = new Date()
      
      // Calcular diferencia en años
      let anios = fechaActual.getFullYear() - fechaIng.getFullYear()
      let meses = fechaActual.getMonth() - fechaIng.getMonth()
      
      // Ajustar si los meses son negativos
      if (meses < 0) {
        anios--
        meses += 12
      }
      
      // Ajustar por días si es necesario
      if (fechaActual.getDate() < fechaIng.getDate()) {
        if (meses === 0) {
          anios--
          meses = 11
        } else {
          meses--
        }
      }
      
      // Formatear el resultado para las tarjetas
      if (anios === 0) {
        return meses === 1 ? '1 mes' : `${meses} meses`
      } else if (meses === 0) {
        return anios === 1 ? '1 año' : `${anios} años`
      } else {
        const anioTexto = anios === 1 ? '1 año' : `${anios} años`
        const mesTexto = meses === 1 ? '1 mes' : `${meses} meses`
        return `${anioTexto} y ${mesTexto}`
      }
    } catch (error) {
      console.error('Error al calcular años de servicio:', error)
      return null
    }
  }

  useEffect(() => {
    document.title = `Perfiles de Empleados - ${NAME_PROJECT}`
    cargarCargos()
    
    // Verificar si hay filtro en la URL antes de cargar perfiles
    const urlParams = new URLSearchParams(window.location.search)
    const cargoFromUrl = urlParams.get('cargo')
    
    if (!cargoFromUrl) {
      // Solo cargar todos los perfiles si no hay filtro en URL
      cargarPerfiles()
    }
  }, [])

  // Efecto separado para cargar filtro desde URL cuando los cargos estén disponibles
  useEffect(() => {
    if (cargos.length > 0) {
      cargarFiltroDesdeURL()
    }
  }, [cargos])

  return (
    <div className='App-content' style={{ height: '100%', overflowY: 'auto' }}>
      <Heading className='mt-0 w-100 heading heading-title' style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'white' }}>
        <div className='heading-text w-100'>
          <i className='bx bxs-user-badge me-2'></i>
          <span>Perfiles de Empleados</span>
        </div>

        <Autocomplete
          id="cboCargos"
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={[
            { id: '', label: 'TODOS LOS CARGOS' },
            ...cargos
          ]}
          fullWidth
          className='me-2 text-transform-none'
          value={cargoSeleccionado}
          onChange={handleCargoChange}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Filtrar por Cargo" 
              className='text-transform-none' 
            />
          )}
        />
      </Heading>

      {/* Información del filtro */}
      <Grid container spacing={1} className='pb-2 heading-buttons justify-content-center heading-subtitle'>
        <Grid item xs={12} md={12}>
          <Typography component='div' className='col-md-12 col-info text-center'>
            {cargoSeleccionado && cargoSeleccionado.id ? 
              `Mostrando empleados con cargo: ${cargoSeleccionado.label}` : 
              'Mostrando todos los empleados'
            }
          </Typography>
        </Grid>
      </Grid>

      {/* Contenido principal */}
      <div style={{ minHeight: 'calc(100vh - 300px)', paddingBottom: '2rem' }}>
        {loading ? (
          <Grid container spacing={1} paddingX={1} height='100%' textAlign='center' alignItems='center'>
            <Grid item xs={12}>
              <CircularProgress color='inherit' />
            </Grid>
          </Grid>
        ) : error ? (
          <Alert severity="info" sx={{ mb: 2, mx: 1 }}>
            {error}
          </Alert>
        ) : (
          <Grid container spacing={1} paddingX={1} paddingTop={1} marginLeft={0} sx={{ width: '100%' }}>
            {/* Renderizado condicional: agrupado por área o normal */}
            {!cargoSeleccionado || !cargoSeleccionado.id ? (
              // Renderizado agrupado por área (TODOS LOS CARGOS)
              perfiles.map((grupo, grupoIndex) => (
                <Grid item xs={12} key={`grupo-${grupoIndex}`}>
                  {/* Título del área */}
                  <Box sx={{ 
                    mb: 2, 
                    mt: grupoIndex > 0 ? 4 : 0,
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'white',
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {grupo.area}
                    </Typography>
                  </Box>
                  
                  {/* Tarjetas de empleados de esta área */}
                  <Grid container spacing={1} sx={{ mb: 3 }}>
                    {grupo.empleados.map((perfil, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`${grupoIndex}-${index}`} sx={{ minWidth: { sm: '280px', md: '250px', lg: '220px' } }}>
                        <Card 
                          sx={{ 
                            height: '100%', 
                            width: '100%',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 3
                            }
                          }}
                          onClick={() => abrirPerfilEmpleado(perfil.CodEmpleado)}
                        >
                          <CardContent sx={{ p: 2 }}>
                            {/* Foto del empleado */}
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                              {perfil.ImagenBase ? (
                                <Avatar
                                  src={perfil.ImagenBase}
                                  sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                                />
                              ) : (
                                <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}>
                                  <PersonIcon sx={{ fontSize: 30 }} />
                                </Avatar>
                              )}
                            </Box>

                            {/* Información básica */}
                            <Typography variant="h6" component="div" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                              {perfil.Nombres}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                              <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                              {perfil.CodEmpleado}
                            </Typography>

                            <Chip 
                              label={perfil.Cargo || 'Sin cargo'} 
                              color="primary" 
                              size="small" 
                              sx={{ mb: 1, fontSize: '0.75rem' }}
                            />

                            {/* Información adicional compacta */}
                            <Box sx={{ mt: 1 }}>
                              {perfil.Area && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  <WorkIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {perfil.Area}
                                </Typography>
                              )}

                              {perfil.Email && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }} noWrap>
                                  <EmailIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {perfil.Email}
                                </Typography>
                              )}

                              {perfil.Telefono && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  <PhoneIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {perfil.Telefono}
                                </Typography>
                              )}

                              {perfil.FechaIng && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Ingreso: {Formatter.dateFormatter(perfil.FechaIng)}
                                </Typography>
                              )}
                              
                              {/* Años de servicio */}
                              {perfil.FechaIng && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  <WorkIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                  {calcularAniosServicio(perfil.FechaIng)}
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))
            ) : (
              // Renderizado normal (cargo específico seleccionado)
              perfiles.map((perfil, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index} sx={{ minWidth: { sm: '280px', md: '250px', lg: '220px' } }}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => abrirPerfilEmpleado(perfil.CodEmpleado)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      {/* Foto del empleado */}
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        {perfil.ImagenBase ? (
                          <Avatar
                            src={perfil.ImagenBase}
                            sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 30 }} />
                          </Avatar>
                        )}
                      </Box>

                      {/* Información básica */}
                      <Typography variant="h6" component="div" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {perfil.Nombres}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                        <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        {perfil.CodEmpleado}
                      </Typography>

                      <Chip 
                        label={perfil.Cargo || 'Sin cargo'} 
                        color="primary" 
                        size="small" 
                        sx={{ mb: 1, fontSize: '0.75rem' }}
                      />

                      {/* Información adicional compacta */}
                      <Box sx={{ mt: 1 }}>
                        {perfil.Area && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            <WorkIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {perfil.Area}
                          </Typography>
                        )}

                        {perfil.Email && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }} noWrap>
                            <EmailIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {perfil.Email}
                          </Typography>
                        )}

                        {perfil.Telefono && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {perfil.Telefono}
                          </Typography>
                        )}

                        {perfil.FechaIng && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Ingreso: {Formatter.dateFormatter(perfil.FechaIng)}
                          </Typography>
                        )}
                        
                        {/* Años de servicio */}
                        {perfil.FechaIng && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            <WorkIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {calcularAniosServicio(perfil.FechaIng)}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {/* Mensaje cuando no hay perfiles */}
        {!loading && !error && perfiles.length === 0 && (
          <Box textAlign="center" p={4}>
            <Typography variant="h6" color="text.secondary">
              No se encontraron empleados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Intenta cambiar el filtro de cargo
            </Typography>
          </Box>
        )}
      </div>
    </div>
  )
}
